import React, { useState, useRef, useEffect } from 'react';
import { api } from '../../lib/api';
import { useStore } from '../../context/StoreContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { exportJson, exportCsv } from '../../lib/exportUtils';
import * as FiIcons from 'react-icons/fi';

const { FiAlertTriangle, FiDownloadCloud, FiDownload, FiFileText, FiUploadCloud, FiTrash2, FiPieChart } = FiIcons;

export const Settings = () => {
  const { currentEvent, staffName } = useStore();
  const [audienceSummary, setAudienceSummary] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteInput, setDeleteInput] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  
  const restoreInputRef = useRef(null);

  useEffect(() => {
    if (currentEvent?.event_id) {
      api.getAudienceSummaryStats(currentEvent.event_id).then(setAudienceSummary);
      
      const unsubscribe = api.subscribeToRegistrations(currentEvent.event_id, () => {
        api.getAudienceSummaryStats(currentEvent.event_id).then(setAudienceSummary);
      });
      return () => { if (unsubscribe) unsubscribe(); };
    }
  }, [currentEvent]);

  const displaySuccess = (msg) => {
    setSuccessMsg(msg);
    setErrorMsg('');
    setTimeout(() => setSuccessMsg(''), 5000);
  };

  const displayError = (msg) => {
    setErrorMsg(msg);
    setSuccessMsg('');
    setTimeout(() => setErrorMsg(''), 5000);
  };

  const handleResetAll = async () => {
    setIsResetting(true);
    const count = await api.resetAllCheckIns(currentEvent.event_id, staffName);
    setIsResetting(false);
    setShowConfirm(false);
    displaySuccess(`Successfully reset ${count} check-ins.`);
  };

  const handleDownloadJson = async () => {
    setIsBackingUp(true);
    try {
      const data = await api.getFullBackup();
      exportJson(data, 'Padua_Midsummer_FULL_BACKUP');
      displaySuccess('Backup downloaded successfully.');
    } catch (err) {
      displayError('Backup could not be created.');
    } finally {
      setIsBackingUp(false);
    }
  };

  const handleDownloadCsv = async (filterType = 'All') => {
    setIsBackingUp(true);
    try {
      const data = await api.getGuestListCsvData(currentEvent.event_id, filterType);
      
      let prefix = 'Padua_Midsummer_Master_Guest_List';
      if (filterType !== 'All') {
        prefix = `Padua_Midsummer_${filterType.replace(/[^a-zA-Z0-9]/g, '_')}`;
      }
      
      exportCsv(data, prefix);
      displaySuccess(`${filterType === 'All' ? 'Master Guest List' : filterType} CSV downloaded successfully.`);
    } catch (err) {
      displayError('CSV could not be created.');
    } finally {
      setIsBackingUp(false);
    }
  };

  const handleDownloadSummaryCsv = () => {
    if (!audienceSummary) return;
    setIsBackingUp(true);
    try {
      const getPct = (val) => audienceSummary.total > 0 ? `${((val / audienceSummary.total) * 100).toFixed(1)}%` : '0%';
      
      const summaryData = [
        { Audience: 'Total RSVPs', Count: audienceSummary.total, 'Percentage of Total RSVPs': '100%' },
        { Audience: 'Checked In', Count: audienceSummary.checkedIn, 'Percentage of Total RSVPs': getPct(audienceSummary.checkedIn) },
        { Audience: 'Not Checked In', Count: audienceSummary.notCheckedIn, 'Percentage of Total RSVPs': getPct(audienceSummary.notCheckedIn) },
        { Audience: 'Booked Padua Couples', Count: audienceSummary.booked, 'Percentage of Total RSVPs': getPct(audienceSummary.booked) },
        { Audience: 'Prospective Couples', Count: audienceSummary.prospective, 'Percentage of Total RSVPs': getPct(audienceSummary.prospective) },
        { Audience: 'Never Toured', Count: audienceSummary.neverToured, 'Percentage of Total RSVPs': getPct(audienceSummary.neverToured) },
        { Audience: 'Previously Toured', Count: audienceSummary.previouslyToured, 'Percentage of Total RSVPs': getPct(audienceSummary.previouslyToured) },
        { Audience: 'Tour Scheduled', Count: audienceSummary.tourScheduled, 'Percentage of Total RSVPs': getPct(audienceSummary.tourScheduled) },
        { Audience: 'Walk-Ins', Count: audienceSummary.walkIns, 'Percentage of Total RSVPs': getPct(audienceSummary.walkIns) },
        { Audience: 'Guests / Family / Other', Count: audienceSummary.guestsFamilyOther, 'Percentage of Total RSVPs': getPct(audienceSummary.guestsFamilyOther) },
        { Audience: 'Vendors', Count: audienceSummary.vendors, 'Percentage of Total RSVPs': getPct(audienceSummary.vendors) }
      ];
      
      exportCsv(summaryData, 'Padua_Midsummer_Audience_Summary');
      displaySuccess('Audience Summary CSV downloaded successfully.');
    } catch (err) {
      displayError('CSV could not be created.');
    } finally {
      setIsBackingUp(false);
    }
  };

  const handleRestoreFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const data = JSON.parse(event.target.result);
        if (!data.backup_metadata) throw new Error('Invalid backup format');
        
        if (window.confirm(`RESTORE BACKUP\n\nAre you sure you want to REPLACE CURRENT DATA with this backup from ${new Date(data.backup_metadata.backup_created_at).toLocaleString()}?\n\nThis will erase all current local data.`)) {
          await api.restoreBackup(data);
          displaySuccess('Backup restored successfully!');
        }
      } catch (err) {
        displayError('Failed to parse backup file. Ensure it is a valid JSON backup.');
      }
    };
    reader.readAsText(file);
    e.target.value = null;
  };

  const handleDeleteAllData = async () => {
    if (deleteInput === 'DELETE MIDSUMMER DATA') {
      await api.dangerousDeleteAllData();
      setShowDeleteConfirm(false);
      setDeleteInput('');
      displaySuccess('ALL LOCAL DATA DELETED.');
      setTimeout(() => window.location.reload(), 2000);
    }
  };

  const StatBox = ({ label, value }) => (
    <div className="flex flex-col">
      <span className="text-2xl font-bold text-gray-800">{value}</span>
      <span className="text-xs text-gray-500 uppercase tracking-wider font-medium">{label}</span>
    </div>
  );

  return (
    <div className="space-y-8 animate-in fade-in">
      <div>
        <h1 className="text-2xl font-cinzel text-padua-green">Settings</h1>
        <p className="text-gray-500 mt-1">Manage local iPad data and administrative tools.</p>
      </div>

      <div className="bg-yellow-50 text-yellow-800 p-4 rounded-xl text-sm font-medium border border-yellow-200">
        <strong>Note:</strong> Data is stored locally on this device. Do not use Private Browsing. Ensure you download backups frequently.
      </div>

      {successMsg && <div className="bg-green-50 text-green-700 p-4 rounded-xl flex items-center font-medium">{successMsg}</div>}
      {errorMsg && <div className="bg-red-50 text-red-700 p-4 rounded-xl flex items-center font-medium">{errorMsg}</div>}

      {/* Post-Event Exports & Audience Summary */}
      <Card className="p-8 border-l-4 border-l-padua-green">
        <div className="flex flex-col md:flex-row items-start gap-6">
          <div className="p-3 bg-green-50 text-padua-green rounded-lg">
            <FiPieChart className="text-2xl" />
          </div>
          <div className="flex-1 w-full">
            <h2 className="text-xl font-cinzel text-padua-green mb-2">EVENT AUDIENCE SUMMARY</h2>
            <p className="text-gray-500 mb-6 max-w-2xl text-sm">
              Review current attendance metrics and download comprehensive post-event CSV reports.
            </p>

            {audienceSummary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8 bg-gray-50 p-6 rounded-xl border border-gray-100">
                <StatBox label="Total RSVPs" value={audienceSummary.total} />
                <StatBox label="Checked In" value={audienceSummary.checkedIn} />
                <StatBox label="Not Checked In" value={audienceSummary.notCheckedIn} />
                <StatBox label="Walk-Ins" value={audienceSummary.walkIns} />
                <StatBox label="Booked Couples" value={audienceSummary.booked} />
                <StatBox label="Prospective Couples" value={audienceSummary.prospective} />
                <StatBox label="Never Toured" value={audienceSummary.neverToured} />
                <StatBox label="Previously Toured" value={audienceSummary.previouslyToured} />
                <StatBox label="Tour Scheduled" value={audienceSummary.tourScheduled} />
                <StatBox label="Guests / Family" value={audienceSummary.guestsFamilyOther} />
                <StatBox label="Vendors" value={audienceSummary.vendors} />
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <Button variant="primary" onClick={() => handleDownloadCsv('All')} disabled={isBackingUp} className="w-full sm:w-auto text-sm">
                <FiDownload className="mr-2" /> DOWNLOAD MASTER GUEST LIST CSV
              </Button>
              <Button variant="outline" onClick={handleDownloadSummaryCsv} disabled={isBackingUp} className="w-full sm:w-auto text-sm">
                <FiFileText className="mr-2" /> DOWNLOAD AUDIENCE SUMMARY CSV
              </Button>
            </div>

            <div className="pt-6 border-t border-gray-100">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Filtered Exports</p>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" className="text-xs py-2 px-3" onClick={() => handleDownloadCsv('Checked-In Guests')} disabled={isBackingUp}>DOWNLOAD CHECKED-IN GUESTS</Button>
                <Button variant="outline" className="text-xs py-2 px-3" onClick={() => handleDownloadCsv('No-Shows')} disabled={isBackingUp}>DOWNLOAD NO-SHOWS</Button>
                <Button variant="outline" className="text-xs py-2 px-3" onClick={() => handleDownloadCsv('Booked Couples')} disabled={isBackingUp}>DOWNLOAD BOOKED COUPLES</Button>
                <Button variant="outline" className="text-xs py-2 px-3" onClick={() => handleDownloadCsv('Prospective Couples')} disabled={isBackingUp}>DOWNLOAD PROSPECTIVE COUPLES</Button>
                <Button variant="outline" className="text-xs py-2 px-3" onClick={() => handleDownloadCsv('Never-Toured Prospects')} disabled={isBackingUp}>DOWNLOAD NEVER-TOURED PROSPECTS</Button>
                <Button variant="outline" className="text-xs py-2 px-3" onClick={() => handleDownloadCsv('Previously-Toured Prospects')} disabled={isBackingUp}>DOWNLOAD PREVIOUSLY-TOURED PROSPECTS</Button>
                <Button variant="outline" className="text-xs py-2 px-3" onClick={() => handleDownloadCsv('Walk-Ins')} disabled={isBackingUp}>DOWNLOAD WALK-INS</Button>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Data Backup Section */}
      <Card className="p-8 border-l-4 border-l-padua-gold">
        <div className="flex flex-col md:flex-row items-start gap-6">
          <div className="p-3 bg-yellow-50 text-padua-gold rounded-lg">
            <FiDownloadCloud className="text-2xl" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-cinzel text-padua-green mb-2">SYSTEM BACKUP</h2>
            <p className="text-gray-500 mb-6 max-w-2xl text-sm">
              Download a complete JSON backup of the local database. This is required for disaster recovery and transferring data between iPads.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <Button variant="primary" onClick={handleDownloadJson} disabled={isBackingUp}>
                <FiDownload className="mr-2" /> DOWNLOAD FULL BACKUP
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Restore Section */}
      <Card className="p-8 border-l-4 border-l-blue-400">
        <div className="flex flex-col md:flex-row items-start gap-6">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
            <FiUploadCloud className="text-2xl" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-cinzel text-padua-green mb-2">RESTORE FROM BACKUP</h2>
            <p className="text-gray-500 mb-6 max-w-2xl text-sm">
              Replace current local data with a previously downloaded JSON backup file.
            </p>
            <input type="file" accept=".json" className="hidden" ref={restoreInputRef} onChange={handleRestoreFileSelect} />
            <Button variant="outline" className="border-blue-200 text-blue-700" onClick={() => restoreInputRef.current?.click()}>
              REPLACE CURRENT DATA
            </Button>
          </div>
        </div>
      </Card>

      {/* Event Testing Tools */}
      <Card className="p-8 border-l-4 border-l-padua-terracotta">
        <div className="flex flex-col md:flex-row items-start gap-6">
          <div className="p-3 bg-red-50 text-red-600 rounded-lg">
            <FiAlertTriangle className="text-2xl" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-cinzel text-padua-green mb-2">EVENT TESTING TOOLS</h2>
            <p className="text-gray-500 mb-6 max-w-2xl text-sm">
              Use with caution before August 23.
            </p>
            
            {!showConfirm ? (
              <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => setShowConfirm(true)}>
                RESET ALL CHECK-INS
              </Button>
            ) : (
              <div className="bg-red-50 p-6 rounded-xl border border-red-100 max-w-lg mb-6">
                <h3 className="text-lg font-medium text-red-800 mb-2">Reset all Midsummer check-ins?</h3>
                <p className="text-sm text-red-600 mb-6">
                  Resets attendance for every registration. Will NOT delete RSVPs or guest data.
                </p>
                <div className="flex gap-4">
                  <Button variant="primary" className="bg-red-600 hover:bg-red-700 text-white border-red-600" onClick={handleResetAll} disabled={isResetting}>
                    {isResetting ? 'RESETTING...' : 'RESET ALL CHECK-INS'}
                  </Button>
                  <Button variant="outline" className="bg-white" onClick={() => setShowConfirm(false)}>CANCEL</Button>
                </div>
              </div>
            )}

            <div className="mt-8 pt-8 border-t border-red-100">
              <h2 className="text-xl font-cinzel text-red-700 mb-2">DELETE ALL LOCAL EVENT DATA</h2>
              
              {!showDeleteConfirm ? (
                <Button variant="outline" className="border-red-600 text-red-600" onClick={() => setShowDeleteConfirm(true)}>
                  <FiTrash2 className="mr-2" /> WIPE IPAD DATABASE
                </Button>
              ) : (
                <div className="bg-red-100 p-6 rounded-xl border border-red-200 max-w-lg">
                  <p className="font-bold text-red-800 mb-4">Type "DELETE MIDSUMMER DATA" to confirm complete local wipe.</p>
                  <input 
                    type="text" 
                    className="w-full p-3 mb-4 rounded border border-red-300"
                    value={deleteInput}
                    onChange={e => setDeleteInput(e.target.value)}
                    placeholder="DELETE MIDSUMMER DATA"
                  />
                  <div className="flex gap-4">
                    <Button 
                      variant="primary" 
                      className="bg-red-800 hover:bg-red-900 text-white" 
                      disabled={deleteInput !== 'DELETE MIDSUMMER DATA'}
                      onClick={handleDeleteAllData}
                    >
                      CONFIRM WIPE
                    </Button>
                    <Button variant="outline" className="bg-white" onClick={() => setShowDeleteConfirm(false)}>CANCEL</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};